import React, { useState } from 'react';
import { Tooltip, Popconfirm, Input, Button } from 'antd';
import {
  PushpinOutlined, PushpinFilled, EnterOutlined, EditOutlined, DeleteOutlined, TagOutlined,
  UserSwitchOutlined, CheckCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import CorpsMessage from './CorpsMessage';
import { EMBLEMES } from '../../services/discussionService';
import { CHARTE_COLORS as C } from '../../variables/constants';

/**
 * Un message du fil interne.
 *
 * LES ACTIONS N'APPARAISSENT QU'AU SURVOL, et c'est délibéré : quatre boutons sous
 * chaque message dans un panneau de 380 px transforment une conversation en tableau
 * de bord. Au survol, la barre se pose en haut à droite de la bulle, sans déplacer
 * le texte - un fil dont les lignes bougent au passage de la souris est illisible.
 *
 * Les droits ne sont pas recalculés ici : `modifiable`, `supprimable` et `epinglable`
 * arrivent du serveur, seul endroit où la fenêtre d'édition et l'habilitation de
 * modération sont connues. Un bouton affiché puis refusé vaut moins que pas de bouton.
 */

const AVATARS = ['#1a6b3c', '#7c3aed', '#b45309', '#1d4ed8', '#be123c', '#0f766e'];

/**
 * Couleur d'avatar dérivée du nom : stable dans le temps et entre écrans, sans avoir
 * à stocker une préférence. On reconnaît un interlocuteur à sa pastille avant d'avoir
 * lu son nom.
 */
const couleurAvatar = (cle = '') => {
  let somme = 0;
  for (let i = 0; i < cle.length; i += 1) somme += cle.charCodeAt(i);
  return AVATARS[somme % AVATARS.length];
};

const heure = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' });
};

/** Pastille des messages système, par acte à l'origine du message. */
const APPARENCE_SYSTEME = {
  AFFECTATION: { icone: UserSwitchOutlined, teinte: { fond: '#e8f5ed', encre: '#1a6b3c' } },
  PRISE_EN_CHARGE: { icone: CheckCircleOutlined, teinte: { fond: '#e8f5ed', encre: '#1a6b3c' } },
  DEFAUT: { icone: InfoCircleOutlined, teinte: { fond: '#eef2f7', encre: '#475569' } },
};

export default function BulleMessage({
  message,
  moiTrackingId,
  peutEcrire,
  surRepondre,
  surReagir,
  surEpingler,
  surModifier,
  surSupprimer,
  surAllerVers,
  surfligne,
}) {
  const { t } = useTranslation('commun');
  const [enEdition, setEnEdition] = useState(false);
  const [brouillon, setBrouillon] = useState(message.corps);
  const [survol, setSurvol] = useState(false);

  const auteur = message.auteur || {};
  const estMien = moiTrackingId && auteur.utilisateurTrackingId === moiTrackingId;

  if (message.supprime) {
    return (
      <div style={{ padding: '6px 0 6px 38px', fontSize: 12, color: C.faint, fontStyle: 'italic' }}>
        {message.supprimePar ? t('bulleMessage.supprimePar', { nom: message.supprimePar }) : t('bulleMessage.supprime')}
      </div>
    );
  }

  // Message posé par l'application, pas tapé par quelqu'un : affectation, prise en
  // charge. Il se distingue par un fond pâle et une pastille d'icône - jamais par
  // un avatar ni une barre d'accent, qui le feraient passer pour une prise de
  // parole. Ni réactions, ni réponse, ni actions : c'est un fait du dossier.
  if (message.typeMessage === 'SYSTEME') {
    const { icone: Icone, teinte } = APPARENCE_SYSTEME[message.evenementType] || APPARENCE_SYSTEME.DEFAUT;
    return (
      <div
        id={`msg-${message.messageTrackingId}`}
        style={{
          display: 'flex', gap: 9, alignItems: 'flex-start',
          margin: '6px 0', padding: '8px 10px', borderRadius: 8,
          background: surfligne ? '#fef9c3' : '#f6f8f7',
        }}
      >
        <span style={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: teinte.fond, color: teinte.encre,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
        }}>
          <Icone />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {message.corps}
          </div>
          <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{heure(message.date)}</div>
        </div>
      </div>
    );
  }

  const validerEdition = async () => {
    const texte = brouillon.trim();
    if (!texte || texte === message.corps) { setEnEdition(false); return; }
    await surModifier?.(message, texte);
    setEnEdition(false);
  };

  return (
    <div
      id={`msg-${message.messageTrackingId}`}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      style={{
        display: 'flex', gap: 9, padding: '7px 8px', borderRadius: 8, position: 'relative',
        background: surfligne ? '#fef9c3' : survol ? C.bg : 'transparent',
        transition: 'background 240ms',
      }}
    >
      {/* Avatar */}
      <span style={{
        width: 29, height: 29, borderRadius: '50%', flexShrink: 0, marginTop: 2,
        background: couleurAvatar(auteur.nom || ''), color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>
        {auteur.initiales || '?'}
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>
            {[auteur.prenoms, auteur.nom].filter(Boolean).join(' ')}
          </span>
          <span style={{ fontSize: 11, color: C.faint }}>{heure(message.date)}</span>
          {message.modifieAt && (
            <Tooltip title={t('bulleMessage.corrigeParAuteur')}>
              <span style={{ fontSize: 10.5, color: C.faint }}>{t('bulleMessage.modifie')}</span>
            </Tooltip>
          )}
          {message.epingle && (
            <Tooltip title={message.epinglePar ? t('bulleMessage.epinglePar', { nom: message.epinglePar }) : t('bulleMessage.epingle')}>
              <PushpinFilled style={{ fontSize: 11, color: C.amber }} />
            </Tooltip>
          )}
        </div>

        {/* Ancre : ce message porte sur un élément précis du dossier */}
        {message.ancreLibelle && (
          <div style={{ marginTop: 3 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe',
              borderRadius: 5, padding: '0 6px', fontSize: 11, fontWeight: 600,
            }}>
              <TagOutlined style={{ fontSize: 10 }} />
              {message.ancreLibelle}
            </span>
          </div>
        )}

        {/* Citation */}
        {message.reponseA && (
          <div
            onClick={() => surAllerVers?.(message.reponseA.messageTrackingId)}
            style={{
              marginTop: 4, paddingLeft: 8, borderLeft: `2px solid ${C.border}`,
              fontSize: 11.5, color: C.muted, cursor: 'pointer',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            <strong style={{ color: C.muted }}>{message.reponseA.auteurNom}</strong>
            {' - '}{message.reponseA.extrait}
          </div>
        )}

        {/* Corps */}
        {enEdition ? (
          <div style={{ marginTop: 5 }}>
            <Input.TextArea
              value={brouillon}
              onChange={(e) => setBrouillon(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 8 }}
              maxLength={5000}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEnEdition(false);
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); validerEdition(); }
              }}
            />
            <div style={{ marginTop: 6, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => { setBrouillon(message.corps); setEnEdition(false); }}>
                {t('bulleMessage.annuler')}
              </Button>
              <Button size="small" type="primary" onClick={validerEdition}
                style={{ background: C.greenMid, borderColor: C.greenMid }}>
                {t('bulleMessage.enregistrer')}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 2, fontSize: 13, color: C.text, lineHeight: 1.55 }}>
            <CorpsMessage corps={message.corps} mentions={message.mentions} moiTrackingId={moiTrackingId} />
          </div>
        )}

        {/* Réactions posées */}
        {message.reactions?.length > 0 && (
          <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
            {message.reactions.map((r) => {
              const meta = EMBLEMES.find((e) => e.code === r.embleme);
              return (
                <Tooltip key={r.embleme} title={r.auteurs?.join(', ')}>
                  <span
                    onClick={() => peutEcrire && surReagir?.(message, r.embleme)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      border: `1px solid ${r.parMoi ? C.greenMid : C.border}`,
                      background: r.parMoi ? C.greenLight : '#fff',
                      borderRadius: 11, padding: '0 7px', height: 21,
                      fontSize: 11.5, cursor: peutEcrire ? 'pointer' : 'default',
                      color: r.parMoi ? C.green : C.muted, fontWeight: 600,
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{meta?.glyphe || '•'}</span>
                    {r.nombre}
                  </span>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>

      {/* Barre d'actions au survol */}
      {survol && !enEdition && peutEcrire && (
        <div style={{
          position: 'absolute', top: -8, right: 8, display: 'flex', gap: 2,
          background: '#fff', border: `1px solid ${C.border}`, borderRadius: 7,
          boxShadow: '0 2px 8px rgba(0,0,0,0.09)', padding: 2, zIndex: 5,
        }}>
          {EMBLEMES.map((e) => (
            <Tooltip key={e.code} title={e.titre}>
              <span
                onClick={() => surReagir?.(message, e.code)}
                style={{
                  width: 24, height: 24, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', cursor: 'pointer', borderRadius: 5, fontSize: 13,
                }}
              >
                {e.glyphe}
              </span>
            </Tooltip>
          ))}
          <span style={{ width: 1, background: C.border, margin: '3px 2px' }} />
          <Tooltip title={t('bulleMessage.repondre')}>
            <span onClick={() => surRepondre?.(message)} style={boutonAction}>
              <EnterOutlined style={{ fontSize: 12, transform: 'scaleX(-1)' }} />
            </span>
          </Tooltip>
          {message.epinglable && (
            <Tooltip title={message.epingle ? t('bulleMessage.retirerEpingles') : t('bulleMessage.epingler')}>
              <span onClick={() => surEpingler?.(message, !message.epingle)} style={boutonAction}>
                {message.epingle
                  ? <PushpinFilled style={{ fontSize: 12, color: C.amber }} />
                  : <PushpinOutlined style={{ fontSize: 12 }} />}
              </span>
            </Tooltip>
          )}
          {message.modifiable && (
            <Tooltip title={t('bulleMessage.modifier')}>
              <span onClick={() => { setBrouillon(message.corps); setEnEdition(true); }} style={boutonAction}>
                <EditOutlined style={{ fontSize: 12 }} />
              </span>
            </Tooltip>
          )}
          {message.supprimable && (
            <Popconfirm
              title={t('bulleMessage.retirerMessage')}
              description={estMien
                ? t('bulleMessage.traceRetrait')
                : t('bulleMessage.retraitCollegue')}
              okText={t('bulleMessage.retirer')}
              cancelText={t('bulleMessage.annuler')}
              okButtonProps={{ danger: true }}
              onConfirm={() => surSupprimer?.(message)}
            >
              <span style={boutonAction}>
                <DeleteOutlined style={{ fontSize: 12, color: C.red }} />
              </span>
            </Popconfirm>
          )}
        </div>
      )}
    </div>
  );
}

const boutonAction = {
  width: 24, height: 24, display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', borderRadius: 5, color: '#6b7280',
};
